/**
 * Pickup Availability Component
 * Fetches store availability from the Storefront API (quantityAvailable)
 * instead of relying on the Liquid available flag.
 * Includes location search (LocationIQ), in-stock filtering, load-more, and add-to-cart.
 */

/* --- Storefront API Client --- */

class StorefrontClient {
  constructor(shopDomain, token, apiVersion = '2025-07') {
    this.endpoint = `https://${shopDomain}/api/${apiVersion}/graphql.json`;
    this.token = token;
  }

  async query(graphql, variables = {}) {
    const res = await fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.token,
      },
      body: JSON.stringify({ query: graphql, variables }),
    });
    if (!res.ok) throw new Error(`Storefront API ${res.status}`);
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e) => e.message).join(', '));
    return json.data;
  }
}

const VARIANT_AVAILABILITY_QUERY = `
  query VariantAvailability($variantId: ID!) {
    node(id: $variantId) {
      ... on ProductVariant {
        storeAvailability(first: 250) {
          nodes {
            available
            quantityAvailable
            pickUpTime
            location {
              id
              name
              address {
                address1
                address2
                city
                province
                zip
                country
                phone
                latitude
                longitude
              }
              metafields(identifiers: [
                { namespace: "store", key: "page_url" },
                { namespace: "store", key: "store_id" }
              ]) {
                key
                value
              }
            }
          }
        }
      }
    }
  }
`;

const ICON_SUCCESS = `<svg role="presentation" focusable="false" stroke-width="2" width="18" height="18" class="text-success icon icon-success" style="--icon-height: 18px" viewBox="0 0 18 18">
  <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
  <path d="M5 8.8L7.62937 11.6L13 6" stroke="#ffffff" fill="none"></path>
</svg>`;


function normalizeAvailabilityNode(node) {
  const loc = node.location;
  const addr = loc.address || {};
  const metafields = (loc.metafields || []).reduce((map, mf) => {
    if (mf) map[mf.key] = mf.value;
    return map;
  }, {});

  return {
    locationId: loc.id,
    name: loc.name,
    address1: addr.address1 || '',
    address2: addr.address2 || '',
    city: addr.city || '',
    province: addr.province || '',
    zip: addr.zip || '',
    country: addr.country || '',
    phone: addr.phone || '',
    lat: parseFloat(addr.latitude) || null,
    lng: parseFloat(addr.longitude) || null,
    quantityAvailable: node.quantityAvailable ?? 0,
    available: node.available,
    pickUpTime: node.pickUpTime || '',
    storePageUrl: metafields.page_url || '',
    storeId: metafields.store_id || '',
    distance: null,
  };
}

/* --- PickupAvailability Web Component --- */

class PickupAvailability extends HTMLElement {
  static get LOCATIONIQ() {
    return {
      GEOCODING_URL: 'https://eu1.locationiq.com/v1/search',
    };
  }

  connectedCallback() {
    const dataEl = this.querySelector('.pickup-availability__storefront-data');
    if (!dataEl) return;

    this.storefrontToken = dataEl.dataset.storefrontToken;
    this.shopDomain = dataEl.dataset.shopDomain;
    this.variantId = dataEl.dataset.variantId;
    this.drawerId = dataEl.dataset.drawerId;
    this.inventoryPolicy = dataEl.dataset.inventoryPolicy || 'deny';
    this.inventoryLocation = dataEl.dataset.inventoryLocation || '';

    try {
      const translationsScript = dataEl.querySelector('script[type="application/json"]');
      this.translations = JSON.parse(translationsScript?.textContent || '{}');
    } catch {
      this.translations = {};
    }

    this.drawer = this.querySelector('.pickup-drawer');
    this.summaryContainer = this.querySelector('.pickup-availability__closest-location');
    this.locationsContainer = this.querySelector('.pickup-availability__locations');
    this.loadMoreButton = this.querySelector('.pickup-availability__load-more');
    this.form = this.querySelector('.pickup-availability__search-form');
    this.messageContainer = this.querySelector('.pickup-availability__message');
    this.messageText = this.querySelector('.pickup-availability__message-text');
    this.filterCheckbox = this.querySelector('.pickup-availability__filter .checkbox');

    this.stores = [];
    this.filteredStores = [];
    this.visibleCount = 4;
    this.isFiltered = false;
    this.isSearched = false;

    const productPrice = parseInt(this.dataset.productPrice, 10);
    this.hideButtons = window.themeVariables?.settings?.hideZeroPrice && productPrice === 0;

    if (this.form) {
      this.form.addEventListener('submit', this.handleSearch.bind(this));
      const input = this.form.querySelector('input[name="location-query"]');
      if (input) {
        input.addEventListener('input', this.handleInputChange.bind(this));
      }
    }
    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', this.handleLoadMore.bind(this));
    }
    if (this.filterCheckbox) {
      this.filterCheckbox.addEventListener('change', this.handleFilterChange.bind(this));
    }

    if (this.storefrontToken && this.shopDomain && this.variantId) {
      this.showSummaryLoading();
      this.loadAvailability();
    } else {
      this.hideSummary();
    }
  }

  get countryCode() {
    return this.dataset.countryCode || 'dk';
  }

  get locationiqApiKey() {
    return window.themeVariables?.locationiqApiKey || '';
  }

  showSummaryLoading() {
    if (this.summaryContainer) {
      this.summaryContainer.innerHTML = '<span class="pickup-availability__search-loading"></span>';
    }
  }

  hideSummary() {
    if (this.summaryContainer) {
      this.summaryContainer.innerHTML = '';
    }
  }

  async loadAvailability() {
    try {
      const client = new StorefrontClient(this.shopDomain, this.storefrontToken);
      const variantGid = `gid://shopify/ProductVariant/${this.variantId}`;
      const data = await client.query(VARIANT_AVAILABILITY_QUERY, { variantId: variantGid });

      const nodes = data?.node?.storeAvailability?.nodes;
      if (!nodes || nodes.length === 0) {
        this.hideSummary();
        return;
      }

      this.stores = nodes.map(normalizeAvailabilityNode);
      this.sortStoresAvailableFirst();
      this.filteredStores = [...this.stores];

      this.renderSummary();
      this.renderList();
      this.updateLoadMoreButton();
    } catch (err) {
      console.error('Pickup availability load error:', err);
      this.hideSummary();
    }
  }

  sortStoresAvailableFirst() {
    this.stores.sort((a, b) => {
      const aInStock = a.quantityAvailable > 0 ? 0 : 1;
      const bInStock = b.quantityAvailable > 0 ? 0 : 1;
      if (aInStock !== bInStock) return aInStock - bInStock;
      return a.name.localeCompare(b.name);
    });
  }

  renderSummary() {
    if (!this.summaryContainer) return;

    const inStockStores = this.stores.filter((s) => s.quantityAvailable > 0);
    const closestAvailable = inStockStores[0];
    const firstStore = this.stores[0];

    let html = '';

    if (closestAvailable) {
      const locationText = this.t('pick_up_available_at').replace('__LOCATION__', closestAvailable.name);
      const storeCount = inStockStores.length;
      const countKey = storeCount === 1 ? 'stores_in_stock_count_one' : 'stores_in_stock_count_other';
      const countText = this.t(countKey).replace('__COUNT__', storeCount);

      const buttonText = this.stores.length === 1
        ? this.t('view_store_info')
        : this.t('check_other_stores');

      html = `
        ${ICON_SUCCESS.replace('icon-success', 'icon-success offset-icon')}
        <div class="pickup-availability__closest-location-info v-stack gap-2">
          <div class="v-stack gap-0.5">
            <p class="bold">${locationText}</p>
          </div>
          <button type="button" class="text-sm text-subdued justify-self-start" aria-controls="${this.drawerId}" aria-expanded="false">
            <span class="link">${buttonText}</span>
            <span class="pickup-availability__stock-count">(${countText})</span>
          </button>
        </div>
      `;
    } else if (firstStore) {
      const locationText = this.t('pick_up_unavailable_at').replace('__LOCATION__', firstStore.name);
      const buttonText = this.stores.length > 1 ? this.t('check_other_stores') : '';

      html = `
        <div class="pickup-availability__closest-location-info v-stack gap-2">
          <p class="bold">${locationText}</p>
          ${buttonText ? `
            <button type="button" class="text-sm text-subdued justify-self-start" aria-controls="${this.drawerId}" aria-expanded="false">
              <span class="link">${buttonText}</span>
            </button>
          ` : ''}
        </div>
      `;
    }

    this.summaryContainer.innerHTML = html;
  }

  renderList() {
    if (!this.locationsContainer) return;

    const storesToRender = this.isFiltered
      ? this.filteredStores.filter((s) => s.quantityAvailable > 0)
      : this.filteredStores;

    this.locationsContainer.innerHTML = storesToRender
      .map((store, index) => {
        const isAvailable = store.quantityAvailable > 0;
        const hidden = !this.isFiltered && index >= this.visibleCount ? ' hidden' : '';

        const icon = isAvailable ? ICON_SUCCESS : '';
        const statusText = isAvailable
          ? this.t('pick_up_available')
          : this.t('pick_up_currently_unavailable');

        const nameHtml = store.storePageUrl
          ? `<a href="${store.storePageUrl}" class="h6">${store.name}</a>`
          : `<p class="h6">${store.name}</p>`;

        const distanceHtml = store.distance !== null
          ? `<span class="pickup-availability__distance text-sm text-subdued">${this.t('distance_away').replace('__DISTANCE__', store.distance.toFixed(1))}</span>`
          : `<span class="pickup-availability__distance text-sm text-subdued" hidden></span>`;

        const addressLines = [
          [store.address1, store.address2].filter(Boolean).join(', '),
          [store.zip, store.city].filter(Boolean).join(' '),
          store.country,
        ].filter(Boolean);
        const addressHtml = `<p>${addressLines.join('<br>')}</p>`;

        const phoneHtml = store.phone
          ? `<a href="tel:${store.phone}">${store.phone}</a>`
          : '';

        const storePageLink = store.storePageUrl
          ? `<a href="${store.storePageUrl}" class="pickup-availability__store-link text-sm">${this.t('view_store_page')}</a>`
          : '';

        const showAtc = (store.quantityAvailable > 0 || this.inventoryPolicy === 'continue' || this.inventoryLocation === 'DSV') && !this.hideButtons;
        const addToCartButton = showAtc
          ? `<button type="button" is="custom-button" class="pickup-availability__row-atc button button--primary button--sm" data-variant-id="${this.variantId}">${this.t('click_and_collect_button')}</button>`
          : '';

        return `
          <div class="pickup-availability__location"
               data-lat="${store.lat || ''}"
               data-lng="${store.lng || ''}"
               data-store-id="${store.storeId}"
               data-available="${isAvailable}"
               data-quantity="${store.quantityAvailable}"
               ${hidden}>
            <div class="v-stack gap-1.5">
              <div class="pickup-availability__location-header">
                ${nameHtml}
                ${distanceHtml}
              </div>
              <div class="text-with-icon">
                ${icon}
                <span class="text-subdued">${statusText}</span>
              </div>
            </div>
            <div class="text-subdued">
              ${addressHtml}
              ${phoneHtml}
            </div>
            <div class="pickup-availability__location-actions">
              ${storePageLink}
              ${addToCartButton}
            </div>
          </div>
        `;
      })
      .join('');

    this.bindAddToCartButtons();
  }

  bindAddToCartButtons() {
    this.locationsContainer.querySelectorAll('.pickup-availability__row-atc').forEach((button) => {
      button.addEventListener('click', (event) => this.handleAddToCart(event.currentTarget));
    });
  }

  t(key) {
    return this.translations[key] || key;
  }

  /* --- Search --- */

  setLoading(isLoading) {
    const submitButton = this.form?.querySelector('button[type="submit"]');
    const buttonText = this.form?.querySelector('.pickup-availability__search-text');
    const buttonLoading = this.form?.querySelector('.pickup-availability__search-loading');

    if (isLoading) {
      submitButton?.setAttribute('disabled', 'true');
      if (buttonText) buttonText.hidden = true;
      if (buttonLoading) buttonLoading.hidden = false;
    } else {
      submitButton?.removeAttribute('disabled');
      if (buttonText) buttonText.hidden = false;
      if (buttonLoading) buttonLoading.hidden = true;
    }
  }

  showMessage(message, isError = false) {
    if (!this.messageContainer || !this.messageText) return;
    this.messageText.textContent = message;
    this.messageContainer.hidden = false;
    this.messageContainer.classList.toggle('pickup-availability__message--error', isError);
  }

  hideMessage() {
    if (this.messageContainer) {
      this.messageContainer.hidden = true;
    }
  }

  haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  async handleSearch(event) {
    event.preventDefault();
    const input = this.form.querySelector('input[name="location-query"]');
    const query = input?.value?.trim();
    if (!query || !this.locationiqApiKey) return;

    this.setLoading(true);
    this.hideMessage();

    try {
      const url = `${PickupAvailability.LOCATIONIQ.GEOCODING_URL}?key=${this.locationiqApiKey}&q=${encodeURIComponent(query)}&countrycodes=${this.countryCode}&format=json&limit=1`;

      const response = await fetch(url);

      if (!response.ok) {
        this.showNoResults();
        return;
      }

      const results = await response.json();

      if (Array.isArray(results) && results.length > 0) {
        const lat = parseFloat(results[0].lat);
        const lng = parseFloat(results[0].lon);
        this.sortStoresByDistance(lat, lng);
      } else {
        this.showNoResults();
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      this.showError();
    } finally {
      this.setLoading(false);
    }
  }

  sortStoresByDistance(userLat, userLng) {
    for (const store of this.stores) {
      if (store.lat != null && store.lng != null) {
        store.distance = this.haversineDistance(userLat, userLng, store.lat, store.lng);
      } else {
        store.distance = null;
      }
    }

    this.filteredStores = [...this.stores].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
    this.isSearched = true;
    this.visibleCount = 4;

    this.renderList();
    this.updateLoadMoreButton();
    this.hideMessage();
  }

  handleInputChange(event) {
    if (event.target.value.trim() === '' && this.isSearched) {
      this.resetToOriginalOrder();
    }
  }

  resetToOriginalOrder() {
    for (const store of this.stores) {
      store.distance = null;
    }
    this.isSearched = false;
    this.visibleCount = 4;
    this.filteredStores = [...this.stores];

    this.renderList();
    this.updateLoadMoreButton();
    this.hideMessage();
  }

  showNoResults() {
    this.showMessage(this.t('no_results'));
  }

  showError() {
    this.showMessage(this.t('search_error'), true);
  }

  /* --- Load More --- */

  handleLoadMore() {
    this.visibleCount += 4;
    const locationEls = this.locationsContainer.querySelectorAll('.pickup-availability__location');
    locationEls.forEach((el, index) => {
      el.hidden = index >= this.visibleCount;
    });
    this.updateLoadMoreButton();
  }

  updateLoadMoreButton() {
    if (!this.loadMoreButton) return;

    const totalVisible = this.locationsContainer.querySelectorAll('.pickup-availability__location').length;
    const hiddenCount = Math.max(0, totalVisible - this.visibleCount);

    if (hiddenCount > 0 && !this.isFiltered) {
      this.loadMoreButton.hidden = false;
      this.loadMoreButton.dataset.remaining = hiddenCount;
    } else {
      this.loadMoreButton.hidden = true;
    }
  }

  /* --- Filter --- */

  handleFilterChange(event) {
    this.isFiltered = event.target.checked;
    this.renderList();
    this.updateLoadMoreButton();
  }

  /* --- Add to Cart --- */

  async handleAddToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.hasAttribute('aria-busy')) return;

    button.setAttribute('aria-busy', 'true');

    try {
      const formData = new FormData();
      formData.set('id', variantId);
      formData.set('quantity', '1');

      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }));
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', `${Shopify.routes.root}variants/${variantId}`);

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      const responseJson = await response.json();

      if (response.ok) {
        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent['sections'] = responseJson['sections'];

        if (this.drawer) this.drawer.hide();

        this.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
            cart: cartContent,
          },
        }));

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent },
        }));
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (error) {
      console.error('Add to cart error:', error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!window.customElements.get('pickup-availability')) {
  window.customElements.define('pickup-availability', PickupAvailability);
}
